import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TextField, AreaField, EditorDialog, EmptyState, Spinner } from "./AdminShared";

const EMPTY = { slug: "", name: "", icon: "Sparkles", description: "" };
const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export const CategoriesManager = () => {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/categories").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load categories"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    const body = { slug: editing.slug, name: editing.name, icon: editing.icon, description: editing.description };
    try {
      if (editing._orig) await api.put(`/categories/${editing._orig}`, body); else await api.post("/categories", body);
      toast.success(editing._orig ? "Category updated" : "Category created");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setSaving(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await api.delete(`/categories/${c.slug}`); toast.success("Category deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const set = (k) => (v) => setEditing((e) => ({ ...e, [k]: v }));
  const setName = (v) => setEditing((e) => ({ ...e, name: v, slug: e._orig ? e.slug : slugify(v) }));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="categories-manager">
      <div className="p-4 border-b border-slate-100 flex justify-end">
        <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-full bg-gold hover:bg-gold-light text-white" data-testid="category-add-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Category
        </Button>
      </div>
      {!rows ? <Spinner /> : rows.length === 0 ? <EmptyState text="No categories yet." /> : (
        <div className="divide-y divide-slate-100">
          {rows.map((c) => (
            <div key={c.slug} className="p-4 flex items-start gap-4 hover:bg-slate-50" data-testid={`category-row-${c.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-navy-900">{c.name}</span>
                  <Badge variant="outline" className="text-[10px] font-mono">{c.slug}</Badge>
                  <Badge className="text-[10px] bg-navy-900">{c.count} resources</Badge>
                </div>
                <p className="text-sm text-slate-500">{c.description}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setEditing({ ...c, _orig: c.slug })} data-testid={`category-edit-${c.slug}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(c)} data-testid={`category-delete-${c.slug}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditorDialog open onClose={() => setEditing(null)} title={editing._orig ? "Edit Category" : "New Category"} onSave={save} saving={saving} testid="category-editor">
          <TextField label="Name" value={editing.name} onChange={setName} testid="category-name-input" required />
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Slug" value={editing.slug} onChange={set("slug")} testid="category-slug-input" required />
            <TextField label="Icon (lucide name)" value={editing.icon} onChange={set("icon")} testid="category-icon-input" />
          </div>
          <AreaField label="Description" value={editing.description} onChange={set("description")} testid="category-description-input" />
        </EditorDialog>
      )}
    </div>
  );
};
